import { useEffect } from 'react'

import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { PaymentChannel } from '../types/wallet-management'
import { api } from '../utils/api-client'
import { decryptUser } from '../utils/user'

// One failed bootstrap fetch used to leave the deposit modal permanently
// blank for the whole session, so retry a couple of times before giving up.
const RETRY_DELAYS_MS = [2_000, 10_000]

const usePaymentChannels = () => {
  const setPaymentChannels = useBoundStore(
    state => state.payment.setPaymentChannels,
  )
  const username = useBoundStore(state => state.user.username)

  useEffect(() => {
    const user = decryptUser()
    if (!user?.username) return

    let cancelled = false

    // Refetched on every username change (not gated on the list being empty)
    // so switching accounts in the same SPA session can't keep the previous
    // account's channels and limits.
    const fetchChannels = async (attempt = 0) => {
      try {
        const res = await api.post<ApiResponse<PaymentChannel[]>>(
          '/payment.php',
          {
            type: 'onlinepay_list_v1',
            payType: 0,
          },
        )
        const { status, info } = res.data
        if (!cancelled && status === 1) {
          setPaymentChannels(info)
        }
      } catch (error) {
        if (cancelled) return
        const delay = RETRY_DELAYS_MS[attempt]
        if (delay === undefined) {
          console.error('Failed to fetch payment channels', error)
          return
        }
        setTimeout(() => {
          if (!cancelled) fetchChannels(attempt + 1)
        }, delay)
      }
    }

    fetchChannels()

    return () => {
      cancelled = true
    }
  }, [username])
}

export default usePaymentChannels
